/**
 * Admin product-review API layer — PIM Wave P3 Task 3. Typed calls to the
 * SHIPPED PIM Wave P3 Task 1 `/admin/marketplace/products/{id}/review` +
 * `/approve` + `/send-back` routes
 * (`apps/api/garden/features/marketplace/admin_router.py:655-727`), all
 * gated `marketplace.product.approve` (granted content_moderator +
 * super_admin). This task adds NO backend routes.
 *
 * `ProductReviewResponse` mirrors `ProductReviewResponse`
 * (`apps/api/garden/features/marketplace/schemas.py:1018-1034`).
 * `form_schema` is the SAME `FormSchemaResponse` contract the P1 admin
 * FormPreview and P2 seller form both consume against — imported here from
 * the ADMIN `category-bindings/api.ts` copy (this feature is admin-only;
 * reaching into `seller-dashboard/` would be the first cross-persona
 * `admin/` -> `seller-dashboard/` type import in the codebase, so it is
 * deliberately avoided — see the task report).
 *
 * `AttributeValueOut` has no existing admin-side copy (only a
 * seller-dashboard one, coupled to that feature's Zod/form-value layer) so
 * it is hand-written here, mirroring `catalog_schema.schemas.AttributeValueOut`.
 *
 * The queue list re-hits the EXISTING `GET /admin/marketplace/products`
 * list (STORY-008-007), now with the PIM Wave P3 Task 1 `status`/`order`
 * query params layered on — so its response envelope is byte-identical to
 * `AdminProductListResponse` (`products/api.ts`), reused directly rather
 * than re-declared.
 */
import { apiClient } from "@/lib/api-client";
import type {
  AdminProduct,
  AdminProductListResponse,
} from "@/features/admin/products/api";
import type {
  FormSchemaResponse,
  TrilingualText,
} from "@/features/admin/category-bindings/api";

/**
 * One resolved attribute value on a product (mirrors `AttributeValueOut`,
 * `catalog_schema/schemas.py`). Reused by `ProductResponse.attributes` and
 * (here) `ProductReviewResponse.attributes`.
 */
export interface AttributeValueOut {
  field_slug: string;
  field_type: string;
  value: string | number | boolean | null;
  unit: string | null;
  value_std: string | number | null;
  label: TrilingualText;
  option_label: TrilingualText | null;
}

/**
 * `GET /admin/marketplace/products/{id}/review` (mirrors
 * `ProductReviewResponse`). All four fields resolve against the product's
 * PRIMARY category (`product.category_id`) — the frontend cross-references
 * `form_schema`'s required bindings against `attributes` + `missing_required`
 * to draw the ✓/✗ checklist per field.
 */
export interface ProductReviewResponse {
  product: AdminProduct;
  form_schema: FormSchemaResponse;
  attributes: AttributeValueOut[];
  completeness: number;
  missing_required: string[];
}

/** Cursor-paginated queue list — same envelope as the admin products list. */
export type ReviewListResponse = AdminProductListResponse;

export interface ListReviewProductsParams {
  /** A value in `PRODUCT_STATUS_VALUES` — the bucket being viewed. */
  status: string;
  cursor?: string | null;
  limit?: number;
}

/**
 * `GET /admin/marketplace/products?status=...&order=asc&cursor=&limit=50`
 * — always oldest-first (FIFO) for the review queue, regardless of bucket.
 */
export function listReviewProducts(
  params: ListReviewProductsParams,
): Promise<ReviewListResponse> {
  const query = new URLSearchParams();
  query.set("status", params.status);
  query.set("order", "asc");
  if (params.cursor) query.set("cursor", params.cursor);
  query.set("limit", String(params.limit ?? 50));
  return apiClient.get<ReviewListResponse>(
    `/admin/marketplace/products?${query.toString()}`,
  );
}

export function getProductReview(id: string): Promise<ProductReviewResponse> {
  return apiClient.get<ProductReviewResponse>(
    `/admin/marketplace/products/${id}/review`,
  );
}

/** Idempotent — safe to retry. Empty body; Idempotency-Key is automatic. */
export function approveProduct(id: string): Promise<AdminProduct> {
  return apiClient.post<AdminProduct, Record<string, never>>(
    `/admin/marketplace/products/${id}/approve`,
    {},
  );
}

/**
 * Idempotent — safe to retry. `reason` is REQUIRED (min 5, max 500 chars —
 * mirrors `SendBackRequest`) and is shown to the seller.
 */
export function sendBackProduct(
  id: string,
  reason: string,
): Promise<AdminProduct> {
  return apiClient.post<AdminProduct, { reason: string }>(
    `/admin/marketplace/products/${id}/send-back`,
    { reason },
  );
}

export const productReviewKeys = {
  /** `["admin","product-review","list",status]`. */
  list: (status: string) => ["admin", "product-review", "list", status] as const,
  /** `["admin","product-review","detail",id]`. */
  detail: (id: string) => ["admin", "product-review", "detail", id] as const,
} as const;
